import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { getSizeWithinBounds } from '@folklore/size';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement, PlaceholderVideo360 } from '@micromag/core/components';
import { useViewerInteraction, useViewerNavigation, useViewerContext, useScreenSize, useScreenRenderContext, useViewerWebView, usePlaybackContext, useViewerContainer } from '@micromag/core/contexts';
import { useTrackScreenEvent, useTrackScreenMedia, useDevicePixelRatio, useActivityDetector, useDebounce, useAnimationFrame } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps } from '@micromag/core/utils';
import Background from '@micromag/element-background';
import ClosedCaptions from '@micromag/element-closed-captions';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Image from '@micromag/element-image';
import Video from '@micromag/element-video';

/**
 * Locale loader
 */
var packageCache = null;
var useThree = function useThree() {
  // transport
  var _useState = useState({
      "package": packageCache
    }),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPackage = _useState2[0]["package"],
    setLoadedPackage = _useState2[1];
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null) {
      return function () {
        canceled = true;
      };
    }
    import('three').then(function (_ref) {
      var Scene = _ref.Scene,
        PerspectiveCamera = _ref.PerspectiveCamera,
        SphereBufferGeometry = _ref.SphereBufferGeometry,
        VideoTexture = _ref.VideoTexture,
        MeshBasicMaterial = _ref.MeshBasicMaterial,
        Mesh = _ref.Mesh,
        WebGLRenderer = _ref.WebGLRenderer,
        MathUtils = _ref.MathUtils;
      packageCache = {
        Scene: Scene,
        PerspectiveCamera: PerspectiveCamera,
        SphereBufferGeometry: SphereBufferGeometry,
        VideoTexture: VideoTexture,
        MeshBasicMaterial: MeshBasicMaterial,
        Mesh: Mesh,
        WebGLRenderer: WebGLRenderer,
        MathUtils: MathUtils
      };
      if (!canceled) {
        setLoadedPackage({
          "package": packageCache
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

var styles = {"container":"micromag-screen-video-360-container","canvasButton":"micromag-screen-video-360-canvasButton","placeholder":"micromag-screen-video-360-placeholder","inner":"micromag-screen-video-360-inner","canvas":"micromag-screen-video-360-canvas","video":"micromag-screen-video-360-video","background":"micromag-screen-video-360-background","content":"micromag-screen-video-360-content","empty":"micromag-screen-video-360-empty","videoContainer":"micromag-screen-video-360-videoContainer","header":"micromag-screen-video-360-header","bottom":"micromag-screen-video-360-bottom","showVideo":"micromag-screen-video-360-showVideo"};

var propTypes = {
  layout: PropTypes.oneOf(['full']),
  video: PropTypes$1.videoElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  background: PropTypes$1.backgroundElement,
  current: PropTypes.bool,
  preload: PropTypes.bool,
  type: PropTypes.string,
  spacing: PropTypes.number,
  mediaRef: PropTypes.func,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'full',
  video: null,
  header: null,
  footer: null,
  background: null,
  current: true,
  preload: true,
  type: null,
  spacing: 20,
  mediaRef: null,
  className: null
};
var Video360Screen = function Video360Screen(_ref) {
  _ref.layout;
    var video = _ref.video,
    header = _ref.header,
    footer = _ref.footer,
    background = _ref.background,
    current = _ref.current,
    preload = _ref.preload,
    type = _ref.type,
    spacing = _ref.spacing,
    customMediaRef = _ref.mediaRef,
    className = _ref.className;
  var THREE = useThree();
  var trackScreenEvent = useTrackScreenEvent(type);
  var trackScreenMedia = useTrackScreenMedia('video_360');
  var _useViewerInteraction = useViewerInteraction(),
    enableInteraction = _useViewerInteraction.enableInteraction,
    disableInteraction = _useViewerInteraction.disableInteraction;
  var _useViewerNavigation = useViewerNavigation(),
    gotoPreviousScreen = _useViewerNavigation.gotoPreviousScreen,
    gotoNextScreen = _useViewerNavigation.gotoNextScreen;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    landscape = _useScreenSize.landscape,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var devicePixelRatio = useDevicePixelRatio();
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var _ref2 = video || {},
    _ref2$media = _ref2.media,
    videoMedia = _ref2$media === void 0 ? null : _ref2$media,
    _ref2$closedCaptions = _ref2.closedCaptions,
    closedCaptions = _ref2$closedCaptions === void 0 ? null : _ref2$closedCaptions,
    _ref2$captions = _ref2.captions,
    captions = _ref2$captions === void 0 ? null : _ref2$captions,
    _ref2$withSeekBar = _ref2.withSeekBar,
    withSeekBar = _ref2$withSeekBar === void 0 ? false : _ref2$withSeekBar,
    _ref2$withControls = _ref2.withControls,
    withControls = _ref2$withControls === void 0 ? false : _ref2$withControls,
    _ref2$autoPlay = _ref2.autoPlay,
    autoPlay = _ref2$autoPlay === void 0 ? true : _ref2$autoPlay,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? null : _ref2$color,
    _ref2$progressColor = _ref2.progressColor,
    progressColor = _ref2$progressColor === void 0 ? null : _ref2$progressColor;
  var videoContainerRef = useRef();
  var _usePlaybackContext = usePlaybackContext(),
    playing = _usePlaybackContext.playing,
    muted = _usePlaybackContext.muted,
    setControls = _usePlaybackContext.setControls,
    setControlsTheme = _usePlaybackContext.setControlsTheme,
    setControlsSuggestPlay = _usePlaybackContext.setControlsSuggestPlay,
    setMedia = _usePlaybackContext.setMedia,
    setPlaying = _usePlaybackContext.setPlaying,
    showControls = _usePlaybackContext.showControls,
    hideControls = _usePlaybackContext.hideControls,
    currentQualityLevel = _usePlaybackContext.currentQualityLevel,
    setCurrentQualityLevel = _usePlaybackContext.setCurrentQualityLevel;
  var mediaRef = useRef(null);
  useEffect(function () {
    if (!current) {
      return function () {};
    }
    if (withControls || withSeekBar) {
      setControls(true);
      setControlsTheme({
        seekBarOnly: withSeekBar && !withControls,
        color: color,
        progressColor: progressColor
      });
    } else {
      setControls(false);
    }
    return function () {
      if (withControls || withSeekBar) {
        setControls(false);
      }
    };
  }, [current, withControls, setControls, withSeekBar, color, progressColor]);
  useEffect(function () {
    if (!current) {
      return function () {};
    }
    setMedia(mediaRef.current);
    return function () {
      setMedia(null);
    };
  }, [current]);
  useEffect(function () {
    if (customMediaRef !== null) {
      customMediaRef(mediaRef.current);
    }
  }, [mediaRef.current]);
  useEffect(function () {
    if (current && autoPlay && !playing) {
      setPlaying(true);
    }
  }, [current, autoPlay]);
  var viewerContainer = useViewerContainer();
  var _useActivityDetector = useActivityDetector({
      element: viewerContainer,
      disabled: !isView,
      timeout: 2000
    }),
    activityDetected = _useActivityDetector.detected;
  var toggleControlsVisibility = useCallback(function () {
    if (activityDetected) {
      showControls();
    } else {
      hideControls();
    }
  }, [activityDetected, showControls, hideControls]);
  useDebounce(toggleControlsVisibility, activityDetected, 1000);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    currentTime = _useState2[0],
    setCurrentTime = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    duration = _useState4[0],
    setDuration = _useState4[1];
  var onTimeUpdate = useCallback(function (time) {
    setCurrentTime(time);
  }, [setDuration, duration]);
  var onProgressStep = useCallback(function (step, meta) {
    trackScreenMedia(videoMedia, "progress_".concat(Math.round(step * 100, 10), "%"), meta);
  }, [trackScreenMedia, videoMedia]);
  var onDurationChange = useCallback(function (dur) {
    setDuration(dur);
  }, [setDuration]);
  var onPlay = useCallback(function (_ref3) {
    var initial = _ref3.initial;
    trackScreenMedia(videoMedia, initial ? 'play' : 'resume');
  }, [trackScreenMedia, videoMedia]);
  var onPause = useCallback(function (_ref4) {
    var midway = _ref4.midway;
    trackScreenMedia(videoMedia, midway ? 'pause' : 'ended');
  }, [trackScreenMedia, videoMedia]);
  var onSeeked = useCallback(function (time) {
    if (time > 0) {
      trackScreenMedia(videoMedia, 'seek');
    }
  }, [trackScreenMedia, videoMedia]);
  var onEnded = useCallback(function () {
    if (current) {
      setPlaying(false);
    }
  }, [current, setPlaying]);

  // ------------------------------------

  var hasVideo = video !== null;
  var _useState5 = useState(!hasVideo),
    _useState6 = _slicedToArray(_useState5, 2),
    ready = _useState6[0],
    setReady = _useState6[1];

  // const transitionPlaying = current && ready;
  // const transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;

  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview,
    enableInteraction: enableInteraction,
    disableInteraction: disableInteraction
  });
  var finalVideo = hasVideo ? _objectSpread(_objectSpread({}, video), {}, {
    autoPlay: !isPreview && !isStatic && !isCapture && autoPlay && current
  }) : null;
  var _ref5 = videoMedia || {},
    _ref5$metadata = _ref5.metadata,
    videoMetadata = _ref5$metadata === void 0 ? null : _ref5$metadata,
    _ref5$url = _ref5.url,
    videoUrl = _ref5$url === void 0 ? null : _ref5$url,
    _ref5$thumbnail_url = _ref5.thumbnail_url,
    thumbnailUrl = _ref5$thumbnail_url === void 0 ? null : _ref5$thumbnail_url;
  var hasVideoUrl = videoUrl !== null;
  var _ref6 = videoMetadata || {},
    _ref6$width = _ref6.width,
    videoWidth = _ref6$width === void 0 ? 0 : _ref6$width,
    _ref6$height = _ref6.height,
    videoHeight = _ref6$height === void 0 ? 0 : _ref6$height;
  var hasThumbnail = thumbnailUrl !== null;
  var _useState7 = useState(!hasThumbnail),
    _useState8 = _slicedToArray(_useState7, 2),
    posterReady = _useState8[0],
    setPosterReady = _useState8[1];
  var withVideoSphere = hasVideoUrl && (isView || isEdit) && !isCapture && !isStatic;
  var _getSizeWithinBounds = getSizeWithinBounds(videoWidth, videoHeight, width, height, {
      cover: true
    }),
    resizedVideoWidth = _getSizeWithinBounds.width,
    resizedVideoHeight = _getSizeWithinBounds.height;
  var resizedVideoLeft = -(resizedVideoWidth - width) / 2;
  var resizedVideoTop = -(resizedVideoHeight - height) / 2;
  useEffect(function () {
    setReady(!hasVideoUrl);
  }, [videoUrl, hasVideoUrl, setReady]);
  useEffect(function () {
    setPosterReady(!hasThumbnail);
  }, [thumbnailUrl, hasThumbnail, setPosterReady]);
  var onVideoReady = useCallback(function () {
    setReady(true);
  }, [setReady]);
  var onPosterLoaded = useCallback(function () {
    setPosterReady(true);
  }, [posterReady]);

  // 3D layer  --------------------------

  var canvasRef = useRef(null);
  var camera = useRef(null);
  var scene = useRef(null);
  var renderer = useRef(null);
  var lon = useRef(0);
  var lat = useRef(0);
  var phi = useRef(0);
  var theta = useRef(0);
  var distance = useRef(50);
  var pointerDown = useRef(false);
  var pointerDownX = useRef(0);
  var pointerDownY = useRef(0);
  var pointerLon = useRef(0);
  var pointerLat = useRef(0);

  // render 3D frame

  var render3D = useCallback(function () {
    if (THREE === null) {
      return;
    }
    var MathUtils = THREE.MathUtils;
    lat.current = Math.max(-85, Math.min(85, lat.current));
    phi.current = MathUtils.degToRad(90 - lat.current);
    theta.current = MathUtils.degToRad(lon.current);
    camera.current.position.x = distance.current * Math.sin(phi.current) * Math.cos(theta.current);
    camera.current.position.y = distance.current * Math.cos(phi.current);
    camera.current.position.z = distance.current * Math.sin(phi.current) * Math.sin(theta.current);
    camera.current.lookAt(0, 0, 0);
    renderer.current.render(scene.current, camera.current);
  }, []);

  // Init 3D layer

  useEffect(function () {
    if (THREE !== null && hasVideoUrl && withVideoSphere) {
      var _ref7 = THREE || {},
        _ref7$Scene = _ref7.Scene,
        Scene = _ref7$Scene === void 0 ? null : _ref7$Scene,
        _ref7$PerspectiveCame = _ref7.PerspectiveCamera,
        PerspectiveCamera = _ref7$PerspectiveCame === void 0 ? null : _ref7$PerspectiveCame,
        _ref7$SphereBufferGeo = _ref7.SphereBufferGeometry,
        SphereBufferGeometry = _ref7$SphereBufferGeo === void 0 ? null : _ref7$SphereBufferGeo,
        _ref7$VideoTexture = _ref7.VideoTexture,
        VideoTexture = _ref7$VideoTexture === void 0 ? null : _ref7$VideoTexture,
        _ref7$MeshBasicMateri = _ref7.MeshBasicMaterial,
        MeshBasicMaterial = _ref7$MeshBasicMateri === void 0 ? null : _ref7$MeshBasicMateri,
        _ref7$Mesh = _ref7.Mesh,
        Mesh = _ref7$Mesh === void 0 ? null : _ref7$Mesh,
        _ref7$WebGLRenderer = _ref7.WebGLRenderer,
        WebGLRenderer = _ref7$WebGLRenderer === void 0 ? null : _ref7$WebGLRenderer;
      if (Scene === null || PerspectiveCamera === null || SphereBufferGeometry === null || VideoTexture === null || MeshBasicMaterial === null || Mesh === null || WebGLRenderer === null) {
        return function () {
          camera.current = null;
          scene.current = null;
          renderer.current = null;
        };
      }
      var _canvasRef$current = canvasRef.current,
        canvasWidth = _canvasRef$current.offsetWidth,
        canvasHeight = _canvasRef$current.offsetHeight;
      camera.current = new PerspectiveCamera(75, canvasWidth / canvasHeight, 1, 1100);
      scene.current = new Scene();
      var geometry = new SphereBufferGeometry(500, 60, 40);
      geometry.scale(-1, 1, 1);
      var _ref8 = mediaRef || {},
        _ref8$current = _ref8.current,
        videoElement = _ref8$current === void 0 ? null : _ref8$current;
      var videoTexture = new VideoTexture(videoElement);
      var videoMaterial = new MeshBasicMaterial({
        map: videoTexture
      });
      var mesh = new Mesh(geometry, videoMaterial);
      scene.current.add(mesh);
      renderer.current = new WebGLRenderer({
        canvas: canvasRef.current
      });
      renderer.current.setPixelRatio(devicePixelRatio);
      renderer.current.setSize(canvasWidth, canvasHeight);
      render3D();
    }
    return function () {
      if (withVideoSphere) {
        camera.current = null;
        scene.current = null;
        renderer.current = null;
      }
    };
  }, [hasVideoUrl, withVideoSphere, render3D]);
  useAnimationFrame(render3D, {
    disabled: !withVideoSphere
  });

  // Resize 3D layer

  useEffect(function () {
    if (camera.current !== null && renderer.current !== null) {
      camera.current.aspect = width / height;
      camera.current.updateProjectionMatrix();
      renderer.current.setSize(width, height);
    }
  }, [width, height]);

  // Pointer interaction
  var togglePlayTimeout = useRef(null);
  var onPointerDown = useCallback(function (e) {
    pointerDown.current = true;
    pointerDownX.current = e.clientX;
    pointerDownY.current = e.clientY;
    pointerLon.current = lon.current;
    pointerLat.current = lat.current;
    // if (togglePlayTimeout.current !== null) {
    //     clearTimeout(togglePlayTimeout.current);
    // }
    // togglePlayTimeout.current = setTimeout(() => {
    //     togglePlay();
    //     togglePlayTimeout.current = null;
    // }, 300);
  }, [
    /* togglePlay */
  ]);
  var pixelsMoved = useRef(0);
  var lastPointerClient = useRef(null);
  var pixelsMovedTracked = useRef(false);
  var onPointerMove = useCallback(function (e) {
    if (pointerDown.current) {
      var _ref9 = e || {},
        _ref9$clientX = _ref9.clientX,
        clientX = _ref9$clientX === void 0 ? null : _ref9$clientX,
        _ref9$clientY = _ref9.clientY,
        clientY = _ref9$clientY === void 0 ? null : _ref9$clientY;
      var downDeltaX = pointerDownX.current - clientX;
      var downDeltaY = pointerDownY.current - clientY;
      lon.current = downDeltaX * 0.2 + pointerLon.current;
      lat.current = downDeltaY * 0.2 + pointerLat.current;
      var _ref0 = lastPointerClient.current || {},
        _ref0$x = _ref0.x,
        lastX = _ref0$x === void 0 ? clientX : _ref0$x,
        _ref0$y = _ref0.y,
        lastY = _ref0$y === void 0 ? clientY : _ref0$y;
      var deltaX = Math.abs(lastX - clientX) || 0;
      var deltaY = Math.abs(lastY - clientY) || 0;
      pixelsMoved.current += deltaX + deltaY;
      if (!pixelsMovedTracked.current && pixelsMoved.current > Math.min(width, height)) {
        trackScreenEvent('drag_sphere', video.name);
        pixelsMovedTracked.current = true;
      }
      lastPointerClient.current = {
        x: clientX,
        y: clientY
      };
      if (Math.abs(downDeltaX) > 3 || Math.abs(downDeltaY) > 3) {
        if (togglePlayTimeout.current !== null) {
          clearTimeout(togglePlayTimeout.current);
          togglePlayTimeout.current = null;
        }
      }
    }
  }, [width, height, trackScreenEvent, video]);
  var onPointerUp = useCallback(function (e) {
    var videoContainer = videoContainerRef.current;
    if (pointerDown.current && videoContainer !== null) {
      var distX = Math.abs(pointerDownX.current - e.clientX);
      var distY = Math.abs(pointerDownY.current - e.clientY);
      var pixelsMovedTolerance = 3;
      var tapNextScreenWidthPercent = 0.5;
      var canTapToNavigate = !landscape && togglePlayTimeout.current !== null;
      var validNavigateTap = canTapToNavigate && distX < pixelsMovedTolerance && distY < pixelsMovedTolerance;
      if (validNavigateTap) {
        var _videoContainer$getBo = videoContainer.getBoundingClientRect(),
          _videoContainer$getBo2 = _videoContainer$getBo.left,
          containerX = _videoContainer$getBo2 === void 0 ? 0 : _videoContainer$getBo2,
          containerWidth = _videoContainer$getBo.width;
        var hasTappedLeft = e.clientX - containerX < containerWidth * (1 - tapNextScreenWidthPercent);
        if (hasTappedLeft) {
          if (gotoPreviousScreen !== null) {
            gotoPreviousScreen();
          }
        } else if (gotoNextScreen !== null) {
          gotoNextScreen();
        }
      }
      if (togglePlayTimeout.current !== null) {
        clearTimeout(togglePlayTimeout.current);
        togglePlayTimeout.current = null;
      }
    }
    pointerDown.current = false;
  }, [gotoPreviousScreen, gotoNextScreen, landscape]);
  var onPlayError = useCallback(function () {
    if (isView && playing && current && hasVideoUrl && autoPlay) {
      setPlaying(false);
      setControlsSuggestPlay(true);
    }
  }, [isView, current, playing, hasVideoUrl, autoPlay, setPlaying, setControlsSuggestPlay]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty({}, className, className !== null), styles.showVideo, isPreview || isStatic || isCapture)]),
    "data-screen-ready": (isStatic || isCapture) && posterReady || ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, withVideoSphere ? /*#__PURE__*/React.createElement("div", {
    ref: videoContainerRef,
    className: styles.videoContainer,
    style: {
      width: resizedVideoWidth,
      height: resizedVideoHeight,
      left: resizedVideoLeft,
      top: resizedVideoTop
    }
  }, /*#__PURE__*/React.createElement(Video, Object.assign({}, finalVideo, {
    mediaRef: mediaRef,
    className: styles.video,
    paused: !current || !playing,
    muted: muted,
    withoutCors: true,
    onReady: onVideoReady,
    onPlay: onPlay,
    onPause: onPause,
    onTimeUpdate: onTimeUpdate,
    onProgressStep: onProgressStep,
    onDurationChange: onDurationChange,
    onSeeked: onSeeked,
    onEnded: onEnded,
    onPosterLoaded: onPosterLoaded,
    onPlayError: onPlayError,
    setPlaying: setPlaying,
    focusable: current && isView,
    shouldLoad: mediaShouldLoad,
    qualityStartLevel: currentQualityLevel,
    onQualityLevelChange: setCurrentQualityLevel
  }))) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.inner
  }, /*#__PURE__*/React.createElement(ScreenElement, {
    key: "video",
    placeholder: /*#__PURE__*/React.createElement(PlaceholderVideo360, {
      className: styles.placeholder,
      width: "100%",
      height: "100%"
    }),
    emptyClassName: styles.empty,
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "oLtEu6",
      defaultMessage: [{
        "type": 0,
        "value": "Video 360"
      }]
    }),
    isEmpty: !withVideoSphere
  }, !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    key: "header",
    className: styles.header,
    style: {
      paddingTop: spacing / 2,
      paddingLeft: spacing,
      paddingRight: spacing,
      transform: !isPreview ? "translate(0, ".concat(viewerTopHeight, "px)") : null
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null, withVideoSphere ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvasRef,
    className: styles.canvas
  }), /*#__PURE__*/React.createElement("button", {
    className: styles.canvasButton,
    type: "button",
    "aria-label": "canvas-interaction",
    onPointerDown: onPointerDown,
    onPointerMove: onPointerMove,
    onPointerUp: onPointerUp,
    tabIndex: current && isView ? null : '-1'
  })) : /*#__PURE__*/React.createElement("div", {
    className: styles.videoContainer,
    style: {
      width: resizedVideoWidth,
      height: resizedVideoHeight,
      left: resizedVideoLeft,
      top: resizedVideoTop
    }
  }, /*#__PURE__*/React.createElement(Image, {
    className: styles.video,
    media: {
      url: thumbnailUrl,
      metadata: {
        width: videoWidth,
        height: videoHeight
      }
    },
    width: resizedVideoWidth,
    height: resizedVideoHeight,
    resolution: resolution
  }))), !isPlaceholder ? /*#__PURE__*/React.createElement("div", {
    key: "bottom-content",
    className: styles.bottom,
    style: {
      transform: current && !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingBottom: spacing / 2,
      paddingTop: 0
    }
  }, (closedCaptions !== null || captions !== null) && !isPreview && !isCapture && !isStatic ? /*#__PURE__*/React.createElement(ClosedCaptions, Object.assign({
    className: styles.closedCaptions,
    media: closedCaptions
  }, captions, {
    currentTime: currentTime
  })) : null, hasFooter ? /*#__PURE__*/React.createElement(Footer, Object.assign({}, footerProps, {
    className: styles.callToAction
  })) : null) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
Video360Screen.propTypes = propTypes;
Video360Screen.defaultProps = defaultProps;

// import * as transforms from './transforms/index';

var definition = {
  id: 'video-360',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "+9akmg",
      defaultMessage: [{
        "type": 0,
        "value": "Audio and Video"
      }]
    }),
    order: 5
  },
  title: defineMessage({
    id: "GRVidW",
    defaultMessage: [{
      "type": 0,
      "value": "360 Video"
    }]
  }),
  component: Video360Screen,
  layouts: ['full'],
  // transforms,
  fields: [{
    name: 'video',
    type: 'video-element',
    theme: {
      color: 'primary'
    },
    defaultValue: {
      autoPlay: true
    },
    label: defineMessage({
      id: "tvl2Zc",
      defaultMessage: [{
        "type": 0,
        "value": "Video"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { Video360Screen, definition as default };
